// VisionMissionGrid.jsx
import React from "react";
import { motion } from "framer-motion";
import { FaCheckCircle } from "react-icons/fa";

const container = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.12,
    },
  },
};

const itemFromLeft = {
  hidden: { opacity: 0, x: -40 },
  show: { opacity: 1, x: 0, transition: { type: "spring", stiffness: 70, damping: 14 } },
};

const itemFromRight = {
  hidden: { opacity: 0, x: 40 },
  show: { opacity: 1, x: 0, transition: { type: "spring", stiffness: 70, damping: 14 } },
};

export default function VisionMissionGrid() {
  const standFor = [
    "Delivering international-standard teaching methods that inspire curiosity and creativity.",
    "Preparing students for global careers while excelling in IIT-JEE, NEET, and other competitive exams.",
    "Fostering values of integrity, empathy, and resilience alongside academic excellence.",
    "Creating a safe, caring, and joyful learning environment where every child thrives.",
  ];

  const headed = [
    "Students who excel academically with confidence and critical thinking.",
    "Young leaders who embrace innovation, global perspectives, and cultural values.",
    "Graduates who are not just successful professionals but also responsible, compassionate individuals.",
    "A community where parents, teachers, and students work together to build a brighter tomorrow.",
  ];

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-gray-50 via-white to-emerald-50">
      {/* subtle background accent */}
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-emerald-200/30 blur-3xl" />
      <div className="pointer-events-none absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-green-300/20 blur-3xl" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-gray-900">
            What We Stand For
          </h2>
          <p className="mt-3 text-gray-600 max-w-3xl mx-auto">
            To provide a world-class, holistic education that prepares children for both national and international success.
          </p>
          <div className="mt-6 mx-auto h-1 w-28 rounded-full bg-emerald-500" />
        </motion.div>

        {/* What We Stand For Grid */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="mt-10 grid gap-6 sm:gap-7 sm:grid-cols-2 lg:grid-cols-4"
        >
          {standFor.map((text, idx) => (
            <motion.div
              key={idx}
              variants={itemFromLeft}
              className="group rounded-2xl border border-emerald-100 bg-white p-5 shadow-sm ring-1 ring-black/5 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-3">
                <div className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 group-hover:bg-emerald-100 transition-colors">
                  <FaCheckCircle className="text-emerald-600 text-xl" />
                </div>
                <p className="text-gray-800 leading-relaxed">{text}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Divider */}
        <div className="my-14 flex items-center gap-3">
          <div className="h-px w-full bg-gradient-to-r from-transparent via-emerald-200 to-transparent" />
          <span className="text-sm uppercase tracking-wider text-emerald-700/70">Next</span>
          <div className="h-px w-full bg-gradient-to-r from-transparent via-emerald-200 to-transparent" />
        </div>

        {/* Where We're Headed */}
        <motion.div
          initial={{ opacity: 0, y: -18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-gray-900">
            Where We’re Headed
          </h2>
          <p className="mt-3 text-gray-600 max-w-3xl mx-auto">
            To be among the most trusted international schools in Hyderabad, shaping students who are future-ready global citizens.
          </p>
          <div className="mt-6 mx-auto h-1 w-28 rounded-full bg-emerald-600" />
        </motion.div>

        {/* Where We're Headed Grid (left-to-right animation feel by alternating variant) */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="mt-10 grid gap-6 sm:gap-7 sm:grid-cols-2 lg:grid-cols-4"
        >
          {headed.map((text, idx) => (
            <motion.div
              key={idx}
              variants={idx % 2 === 0 ? itemFromLeft : itemFromRight}
              className="group rounded-2xl border border-emerald-100 bg-white p-5 shadow-sm ring-1 ring-black/5 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-3">
                <div className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 group-hover:bg-emerald-100 transition-colors">
                  <FaCheckCircle className="text-emerald-700 text-xl" />
                </div>
                <p className="text-gray-800 leading-relaxed">{text}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
